import { JSX, useCallback, useEffect, useState } from "react";
import { Button, Modal, Text, Group } from "@mantine/core";
import { usePostApi } from "../../hooks";
import { getYachtId } from "../../utils";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setVoyageByIndex } from "src/redux/slices/generatorSlice";
import dayjs from "dayjs";

interface UseAmbNewVoyageProps {
  tag?: string;
  getValues?: (payload: string | string[]) => Array<any>;
  setValue?: (id: string, value: any) => void;
  onConfirm?: (confirmed: boolean) => void;
  rowIndex?: number;
}

interface UseAmbNewVoyageReturn {
  onVoyageChange: (value: any) => void;
  modalElement: JSX.Element | null;
}

const useAmbNewVoyage = ({
  tag,
  getValues,
  setValue,
  onConfirm,
  rowIndex = 0,
}: UseAmbNewVoyageProps = {}): UseAmbNewVoyageReturn => {
  const [showModal, setShowModal] = useState(false);
  const { post } = usePostApi();
  const { lbeidOrPartId, lbid } = useParams();
  const dispatch = useDispatch();
  const voyages = useSelector(state => state.generatorSlice.voyages);
  const prevEntryData = useSelector(
    state => state.generatorSlice.lbentry.prevEntryData,
  );
  const previousVoyage = prevEntryData?.list?.previousVoyage || null;

  const isVoyageInput =
    tag &&
    (tag === "amb2-date-start" ||
      tag === "amb2-time-start" ||
      tag === "amb2-date-end" ||
      tag === "amb2-time-end");

  const checkToDateTimeExists = useCallback(() => {
    const findInputByTagAndRow = (tagClass: string, isTime: boolean) => {
      const containers = Array.from(document.querySelectorAll(`.${tagClass}`));

      if (containers[rowIndex]) {
        const selector = isTime ? "input" : "button";
        const input = containers[rowIndex].querySelector(selector);
        const dataId = input?.getAttribute("data-id");
        return dataId ? `${dataId}${isTime ? ".time" : ""}` : null;
      }
      return null;
    };

    const dateTimeIds = [
      findInputByTagAndRow("amb2-date-start", false),
      findInputByTagAndRow("amb2-time-start", true),
      findInputByTagAndRow("amb2-date-end", false),
      findInputByTagAndRow("amb2-time-end", true),
    ].filter(id => id !== null);

    if (getValues && dateTimeIds.length === 4) {
      const values = getValues(dateTimeIds);

      return {
        checkStart: values
          .slice(0, 2)
          .every(
            v => v && (typeof v === "object" ? v instanceof Date : v !== ""),
          ),
        checkEnd: values
          .slice(2, 4)
          .every(
            v => v && (typeof v === "object" ? v instanceof Date : v !== ""),
          ),
        data: values,
      };
    }
    return { checkStart: false, checkEnd: false, data: null };
  }, [getValues, rowIndex]);

  const recordDateTimeChange = useCallback(
    async (v: any) => {
      try {
        const action = `voyage ${tag?.includes("end") ? "TO" : "FROM"} ${tag?.includes("date") ? "date" : "time"}`;
        await post({
          request: "record-history-action",
          body: {
            params: {
              yachtid: getYachtId(),
              lbid,
              lbeid: lbeidOrPartId,
              action: `User has ${v ? `changed ${action} to ${v instanceof Date ? dayjs(v).format("DD MMM YYYY") : v}` : `removed ${action}`}`,
            },
          },
        });
      } catch (error) {
        console.error("Failed to record voyage date/time change", error);
      }
    },
    [tag, post, lbid, lbeidOrPartId],
  );

  const updateVoyageData = useCallback(() => {
    if (!isVoyageInput) return;

    const { checkStart, checkEnd, data } = checkToDateTimeExists();
    if (data === null) return;

    const currentVoyage = voyages[rowIndex] || { start: null, end: null };

    if (checkStart) {
      const timeValue =
        typeof data[1] === "object" && "time" in data[1]
          ? data[1].time
          : data[1];
      const startDateTime = dayjs(data[0])
        .set("hour", parseInt(timeValue.split(":")[0], 10))
        .set("minute", parseInt(timeValue.split(":")[1], 10))
        .format("DD MMM YYYY - HH:mm");

      if (startDateTime !== currentVoyage.start) {
        dispatch(
          setVoyageByIndex({
            rowIndex,
            start: startDateTime,
          }),
        );
      }
    }

    if (checkEnd) {
      const timeValue =
        typeof data[3] === "object" && "time" in data[3]
          ? data[3].time
          : data[3];
      const endDateTime = dayjs(data[2])
        .set("hour", parseInt(timeValue.split(":")[0], 10))
        .set("minute", parseInt(timeValue.split(":")[1], 10))
        .format("DD MMM YYYY - HH:mm");

      if (endDateTime !== currentVoyage.end) {
        dispatch(
          setVoyageByIndex({
            rowIndex,
            end: endDateTime,
          }),
        );
      }
    }
  }, [checkToDateTimeExists, dispatch, rowIndex, voyages, isVoyageInput]);

  const onVoyageChange = useCallback(
    (value: any) => {
      if (!tag || !tag.includes("amb2-")) return;
      recordDateTimeChange(value);
      updateVoyageData();

      if (value && isVoyageInput) {
        const { checkStart, checkEnd } = checkToDateTimeExists();

        if (checkStart && checkEnd && tag.includes("start") && rowIndex === 0) {
          const isNewVoyage =
            !previousVoyage || (previousVoyage && voyages[0]?.end);
          if (isNewVoyage) {
            setShowModal(true);
          }
        }
      }
    },
    [
      tag,
      checkToDateTimeExists,
      recordDateTimeChange,
      updateVoyageData,
      rowIndex,
      isVoyageInput,
      previousVoyage,
    ],
  );

  useEffect(() => {
    updateVoyageData();
  }, [updateVoyageData, lbeidOrPartId]);

  const handleConfirm = useCallback(
    async (confirmed: boolean) => {
      setShowModal(false);

      const startDateContainers = Array.from(
        document.querySelectorAll(".amb2-date-start"),
      );
      const startTimeContainers = Array.from(
        document.querySelectorAll(".amb2-time-start"),
      );

      const startDateId = startDateContainers[rowIndex]
        ?.querySelector("div > button")
        ?.getAttribute("data-id");
      const startTimeId = startTimeContainers[rowIndex]
        ?.querySelector("div > input")
        ?.getAttribute("data-id");

      if (confirmed) {
        let recordedDateTime = new Date().toLocaleString();
        if (startDateId && startTimeId && getValues) {
          const values = getValues([startDateId, startTimeId]);
          if (values.length >= 2 && values[0] && values[1]) {
            let dateValue = "";
            if (values[0] instanceof Date) {
              dateValue = dayjs(values[0]).format("D MMM YYYY");
            } else {
              dateValue = values[0].toString();
            }

            const timeValue =
              typeof values[1] === "object" && "time" in values[1]
                ? values[1].time
                : values[1].toString();

            recordedDateTime = `${dateValue} - ${timeValue}`;
          }
        }

        try {
          await post({
            request: "record-history-action",
            body: {
              params: {
                yachtid: getYachtId(),
                lbid,
                lbeid: lbeidOrPartId,
                action: `User has started a new voyage at ${recordedDateTime}`,
              },
            },
          });
        } catch (error) {
          console.error("Failed to record voyage history:", error);
        }
      } else {
        setValue?.(startDateId || "", null);
        setValue?.(startTimeId || "", { time: "", type: "LT" });
      }
      onConfirm?.(confirmed);
    },
    [onConfirm, post, lbid, lbeidOrPartId, getValues, rowIndex, setValue],
  );

  const modalElement = showModal ? (
    <Modal
      opened={showModal}
      onClose={() => handleConfirm(false)}
      title="New Voyage"
      centered
      size="sm">
      <Text mb="md">Are you starting a new voyage?</Text>
      <Group justify="right">
        <Button variant="outline" onClick={() => handleConfirm(false)}>
          No
        </Button>
        <Button onClick={() => handleConfirm(true)}>Yes</Button>
      </Group>
    </Modal>
  ) : null;

  return {
    onVoyageChange,
    modalElement,
  };
};

export default useAmbNewVoyage;
